import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import { getUser } from "../../State/Auth/Action";
import { removeWishlistItem } from "../../State/Wishlist/Action";
import AuthModal from "../../Auth/AuthModal";
import defaultImage from "../../../Common/productDefaultImage.png";

function WishlistProductCard({ item }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const jwt = localStorage.getItem("jwt");
    const role = localStorage.getItem("role");
    const [openAuthModal, setOpenAuthModal] = useState(false);
    const [slideIndex, setSlideIndex] = useState(0);
    const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } = useConvertPrice();
    const wishlistLoading = useSelector((state) => state.wishlist.loading);
    const { auth } = useSelector((store) => store);

    const handleOpenAuthModal = () => setOpenAuthModal(true);
    const handleCloseAuthModal = () => setOpenAuthModal(false);

    useEffect(() => {
        if (jwt && role === "CUSTOMER") {
            dispatch(getUser(jwt));
        }
    }, [jwt, dispatch, role]);

    const primaryImages = useMemo(() => {
        return item?.images?.filter((img) => img?.isPrimary) || [];
    }, [item?.images]);

    useEffect(() => {
        if (primaryImages.length > 1) {
            const interval = setInterval(() => {
                setSlideIndex((prev) => (prev + 1) % primaryImages.length);
            }, 4000);
            return () => clearInterval(interval);
        }
    }, [primaryImages]);

    const handleRemoveItemFromWishlist = async (e) => {
        e.stopPropagation();
        if (!auth?.user) {
            handleOpenAuthModal();
            return;
        }
        await dispatch(removeWishlistItem(item?.id));
    };

    if (!item || !item.id) {
        return null;
    }

    const currentImage = primaryImages.length > 0
        ? primaryImages[slideIndex]?.fullUrl
        : defaultImage;

    const cheapestVariant = item?.variants?.reduce(
        (min, curr) => (curr?.adminPrice < min?.adminPrice ? curr : min),
        item?.variants?.[0]
    );
    const hasDiscount = cheapestVariant?.viewTotalDiscount > 0;
    const originalPrice = cheapestVariant
        ? convert(customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount))
        : null;
    const discountedPrice = cheapestVariant
        ? payableAmount(
            customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount),
            cheapestVariant.viewTotalDiscount
        )
        : null;

    return (
        <div className="flex flex-col">
            <div
                className="aspect-[3/4] cursor-pointer overflow-hidden rounded-2xl bg-[#f6f2ed]"
                onClick={() => navigate(`/product/${item?.id}`)}
            >
                <img
                    src={currentImage}
                    alt={item?.title || "Product"}
                    className="h-full w-full object-cover"
                />
            </div>

            <div className="mt-3 flex flex-col px-0.5">
                <p
                    className="truncate text-[14px] italic leading-5 text-[#333]"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                    {item?.brand?.name}
                </p>
                <h3 className="mt-0.5 truncate text-[13px] font-light leading-5 text-[#707070]">
                    {item?.title}
                </h3>
                {cheapestVariant && (
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 font-sans text-[13px]">
                        {hasDiscount ? (
                            <>
                                <span className="font-semibold text-[#333]">
                                    {discountedPrice} {selectedCurrency}
                                </span>
                                <span className="text-[#9a9a9a] line-through">
                                    {originalPrice}
                                </span>
                                <span className="font-medium text-[#2f9e44]">
                                    {cheapestVariant.viewTotalDiscount}% OFF
                                </span>
                            </>
                        ) : (
                            <span className="font-semibold text-[#333]">
                                {originalPrice} {selectedCurrency}
                            </span>
                        )}
                    </div>
                )}
                <button
                    disabled={wishlistLoading}
                    className="mt-3 h-10 w-full rounded-lg border border-[#ececec] bg-white text-[11px] font-medium uppercase tracking-[0.08em] text-[#333] hover:border-[#BB3D1F] hover:text-[#BB3D1F] disabled:opacity-60"
                    onClick={handleRemoveItemFromWishlist}
                >
                    Remove From Wishlist
                </button>
            </div>
            <AuthModal handleClose={handleCloseAuthModal} open={openAuthModal} />
        </div>
    );
}

export default WishlistProductCard;
